import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  setBackdropLoadingAction,
  setExportingStatsAction,
} from 'store/actions/mainPage';
import { formatNumber } from 'store/actions/mainPage/constant';
import { ExportStatProps } from 'types/mainPage';
import { exportDataLog, exportFile } from 'utils/exportFile';
import { roundUpToNearest } from 'components/DiagramsTab/constants';
import { Box, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from 'recharts';

export const DiagramForExport: React.FC<ExportStatProps> = ({ setOpen }) => {
  const {
    statisticsData,
    isLogged,
    exportingStats: exporting,
    exportingExt: ext,
    lang,
  } = useAppSelector((state) => state.mainPage);
  const diagrRef = useRef(null);
  const dispatch = useAppDispatch();

  if (!statisticsData) {
    return null;
  }
  const { data } = statisticsData;

  let maxValue = roundUpToNearest(Math.max(...data.map((item) => +item.value)));
  const xAxisTicks =
    maxValue <= 100000
      ? [0, maxValue / 4, (maxValue * 2) / 4, (maxValue * 3) / 4, maxValue]
      : [0, maxValue / 2, maxValue];

  const formattedData = data.map((item: any, index) => {
    return {
      key: item.key,
      value: Math.floor(Number(item.value) * 10) / 10,
      id: index,
      personalUnit: item.unit,
    };
  });
  const { innerWidth } = window;

  useEffect(() => {
    const { filename: fileTitle } = statisticsData.export;

    setTimeout(() => {
      exportFile(diagrRef.current, fileTitle, ext)
        .then(() => exportDataLog(isLogged, fileTitle, ext, 'histograms'))
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          dispatch(setExportingStatsAction(false, ''));
        });
    }, 300);
    return () => {
      dispatch(setBackdropLoadingAction(false));
      setOpen(false);
    };
  }, [exporting]);
  const mainUnit = statisticsData?.export.unit;

  const chartHeight = Math.max(500, formattedData.length * 40);
  const w = innerWidth * 0.9;
  const xw = innerWidth * 0.15;
  const yw = innerWidth * 0.15;
  const yh = innerWidth * 0.035;
  const h = (window.innerHeight * chartHeight) / 500;

  const fz = innerWidth > 5000 ? '8rem' : innerWidth > 3000 ? '3rem' : '1.5rem';
  const titleFz =
    innerWidth > 5000 ? '8.5rem' : innerWidth > 3000 ? '3.5rem' : '1.8rem';
  const yAxisFormatter = (param) => {
    return String(param);
  };

  const CustomizedLabel = (props) => {
    const { x, y, width, value, height, id } = props;
    const item = formattedData.find(
      (item) => item.id === id && item.value === value
    );

    return (
      <text
        x={x + width + 5}
        y={y + height / 2}
        fill="#8884d8"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={fz}
      >
        {formatNumber(value.toString(), lang)} {mainUnit || item?.personalUnit}
      </text>
    );
  };

  return (
    <Box
      ref={diagrRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        position: 'fixed',
        padding: 5,
        left: '9999px',
        svg: {
          overflow: 'initial',
        },
      }}
    >
      <Typography
        sx={{ textAlign: 'center', mb: 2, fontSize: titleFz, fontWeight: 700 }}
      >
        {statisticsData?.export.filename}
      </Typography>
      <Box sx={{ mr: 5 }}>
        <BarChart
          width={w}
          height={h}
          data={formattedData}
          layout="vertical"
          margin={{ right: 100 }}
        >
          <CartesianGrid horizontal={false} vertical={true} />
          <XAxis
            type="number"
            dataKey="value"
            ticks={xAxisTicks}
            tickFormatter={(value) => formatNumber(value.toString(), lang)}
            interval={0}
            domain={[0, maxValue]}
            width={xw}
            height={yh}
            style={{
              fontSize: fz,
            }}
          />
          <YAxis
            dataKey="key"
            type="category"
            tickFormatter={yAxisFormatter}
            width={yw}
            style={{
              fontSize: fz,
            }}
          />
          <Tooltip />
          <Bar
            dataKey="value"
            fill="#66B2FF"
            strokeWidth={1}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="value"
              content={<CustomizedLabel />}
              position="right"
            />
          </Bar>
        </BarChart>
        <Typography
          sx={{
            textAlign: 'center',
            color: 'rgb(102, 102, 102)',
            fontSize: fz,
            mb: 5,
          }}
        >
          {mainUnit}
        </Typography>
      </Box>
    </Box>
  );
};
